<template>
  <div class="nav-bar flex align-items-center justify-content-between w-full px-2 lg:px-4 py-3 sticky top-0 z-5"
       :class="navBarClass">
    <img :src="logo" alt="Logo" class="topnavbar w-8rem lg:w-10rem" @click="goHome" />

    <div class="hidden lg:flex align-items-center gap-2">
      <Button v-if="role" class="p-button-text btn-topbar p-button-sm"
              :class="{ 'active-btn': isActive('/subscription') }" label="Subscription"
              @click="goTo('/subscription')" />
      <Button class="p-button-text btn-topbar p-button-sm" :class="{ 'active-btn': isActive('/becomeTutor') }"
              label="Become a Tutor" @click="goTo('/becomeTutor')" v-if="!isStudent && !isTutor"/>
      <Button class="p-button-text btn-topbar p-button-sm" :class="{ 'active-btn': isActive('/matchTutor') }"
              label="Find Tutor" @click="goTo('/matchTutor')" v-if="!isTutor" />
      <Button class="p-button-text btn-topbar p-button-sm" :class="{ 'active-btn': isActive('/pricing') }"
              label="Pricing" @click="goTo('/pricing')" />
      <Button class="p-button-text btn-topbar p-button-sm" :class="{ 'active-btn': isActive('/aboutUs') }"
              label="About Us" @click="goTo('/aboutUs')" />
      <Button class="p-button-text btn-topbar p-button-sm" :class="{ 'active-btn': isActive('/login') }"
              label="Login" @click="goTo('/login')" v-if="!isLoggedIn" />
      <Button class="p-button-text p-button-sm justify-content-start" :class="joinBtnClass"
              label="Join" @click="goTo('/onBoarding')" v-if="!isLoggedIn" />
      <Button class="p-button-text p-button-sm justify-content-start" label="Dashboard"
              @click="goToDashboard" v-if="showDashboard" />
      <GoogleTranslate class="px-2" />
    </div>

    <Button v-if="!showMobileMenu" @click="showMobileMenu = true" class="lg:hidden p-button-text p-button-sm"
            icon="pi pi-bars" />
    
    <!-- Mobile menu -->
    <div v-if="showMobileMenu" class="lg:hidden fixed top-0 left-0 w-full h-full z-5 bg-white shadow-2">
      <div class="flex justify-content-between align-items-center p-3 border-bottom-1 surface-100">
        <img :src="mobileLogo" alt="Logo" class="w-8rem" />
        <Button @click="showMobileMenu = false" class="p-button-text p-button-sm" icon="pi pi-times" />
      </div>
      <div class="flex flex-column p-3 gap-3">
        <Button v-if="role" class="p-button-text btn-topbar p-button-sm justify-content-start" label="Subscription" />
        <Button v-if="role" class="p-button-text btn-topbar p-button-sm justify-content-start" label="Exam Preparation" @click="isDialogVisible = true"/>
        <Button v-if="!isStudent && !isTutor" class="p-button-text btn-topbar p-button-sm justify-content-start" label="Become a Tutor" @click="goTo('/becomeTutor')" />
        <Button v-if="!isTutor" class="p-button-text btn-topbar p-button-sm justify-content-start" label="Find Tutor" @click="goTo('/matchTutor')" />
        <Button class="p-button-text btn-topbar p-button-sm justify-content-start" label="Pricing" @click="goTo('/pricing')" />
        <Button class="p-button-text btn-topbar p-button-sm justify-content-start" label="About Us" @click="goTo('/aboutUs')" />
        <Button class="p-button-text btn-topbar p-button-sm justify-content-start" label="Login" @click="goTo('/login')" v-if="!isLoggedIn" />
        <Button class="p-button-text p-button-sm justify-content-start" :class="joinBtnClass" label="Join" @click="goTo('/onBoarding')" v-if="!isLoggedIn" />
        <Button class="p-button-text p-button-sm justify-content-start" label="Dashboard" @click="goToDashboard" v-if="showDashboard" />
        <GoogleTranslate />
      </div>
    </div>
    <ComingSoonDialog v-model="isDialogVisible" :duration="3000" />
  </div>
</template>

<script setup>
import { ref, computed ,defineProps, onMounted} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Button from 'primevue/button';
import GoogleTranslate from '@/components/GoogleTranslate.vue';
import { useAuthStore } from '@/stores/authStore';
import topNavlogo from '@/assets/topNavLogo.png';
import MobiletopNavlogo from '@/assets/topNavLogo.png';
import ComingSoonDialog from '@/components/ComingSoonDialog.vue';

const props = defineProps({
  role: { type: String, default: '' }
});

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const showMobileMenu = ref(false);
const isDialogVisible = ref(false);

const isLoggedIn = computed(() => !!authStore.user);
const showDashboard = computed(() => isLoggedIn.value);

const dashboardUrl = computed(() => {
  const role = authStore.user?.role;
  if (role === 'admin' || role === 'super_admin') return '/admin_dashboard';
  if (role === 'student') return '/student_dashboard';
  if (role === 'tutor') return '/tutor_dashboard';
  if (role === 'institute') return '/institution_dashboard';
  return '/login';
});

const onboardingUrl = computed(() => {
  const role = authStore.user?.role;
  if (role === 'student') return '/studentOnBoarding';
  if (role === 'tutor') return '/tutorOnBoarding';
  if (role === 'institute') return '/institutionOnBoarding';
  return '/onBoarding';
});

const goToDashboard = () => {
  showMobileMenu.value = false;
  const user = authStore.user;
  const role = user?.role;
  if (role === 'admin' || role === 'super_admin') {
    router.push('/admin_dashboard');
    return;
  }
  const isComplete = role === 'tutor'
    ? user.registrationCompleted && user.verificationRequested
    : !!user?.registrationCompleted;
  router.push(isComplete ? dashboardUrl.value : onboardingUrl.value);
};

const currentPage = computed(() => route.path);
const isActive = (path) => currentPage.value === path || currentPage.value.startsWith(path + '/');

const goTo = (path) => {
  showMobileMenu.value = false;
  router.push(path);
};

const goHome = () => router.push('/');

const navBarClass = computed(() => {
  if (props.role === 'student') return 'bg-orange-300';
  if (props.role === 'tutor') return 'bg-teal-100';
  if (props.role === 'institution') return 'bg-yellow-500';
  return 'bg-teal-1300';
});

const joinBtnClass = computed(() => {
  if (props.role === 'student') return 'btn-student-outline';
  if (props.role === 'tutor') return 'btn-tutor-outline';
  if (props.role === 'institution') return 'btn-institution-outline';
  return 'btn-default-outline';
});

const isStudent = computed(() => authStore.user?.role === 'student');
const isTutor = computed(() => authStore.user?.role === 'tutor');
// const isInstitution = computed(() => authStore.user?.role === 'institution');
const logo = topNavlogo;
const mobileLogo = MobiletopNavlogo;

onMounted(async () => {
    try{
        const run = localStorage.getItem("TOKEN_CHECKED");
        const hash = window.location.hash;
        const hasOAuthParams = hash.includes('token=') && hash.includes('refreshToken=');
        if(!run && !hasOAuthParams){
            await authStore.refresh();
        }
    }catch(e){
        console.log("token validation failed");
    }
});
</script>
<style scoped>
.nav-bar {
    min-height: 60px;
}

.active-btn {
    border-bottom-color: var(--teal-500) !important;
    border-bottom-left-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
}

.topnavbar {
    height: auto;
    max-width: 160px;
    cursor: pointer;
}

.btn-topbar {
    font-size: 14px;
    font-weight: 500;
    color: var(--gray-600) !important;
}

.btn-topbar:hover {
    background-color: transparent !important;
    transform: scale(1.05);
}

/* Default outline button */
.btn-default-outline {
    border-color: var(--teal-700) !important;
    color: black !important;
}

.btn-default-outline:hover {
    background-color: var(--teal-500) !important;
    color: #fff !important;
}

.btn-student-outline {
    border-color: var(--orange-500) !important;
    color: var(--orange-500) !important;
}

.btn-student-outline:hover {
    background-color: var(--orange-500) !important;
    color: #fff !important;
}

.btn-tutor-outline {
    border-color: var(--teal-500) !important;
    color: var(--teal-500) !important;
}

.btn-tutor-outline:hover {
    background-color: var(--teal-500) !important;
    color: #fff !important;
}

.btn-institution-outline {
    border-color: var(--yellow-500) !important;
    color: var(--yellow-700) !important;
}

.btn-institution-outline:hover {
    background-color: var(--yellow-500) !important;
    color: #fff !important;
}

.bg-orange-300 {
    background-color: var(--orange-700) !important;
}

.bg-teal-100 {
    background-color: var(--teal-100) !important;
}

.bg-yellow-500 {
    background-color: var(--yellow-100) !important;
}

.bg-teal-1300 {
    background-color: var(--teal-1300) !important;
}


/* Mobile Styles */
@media (max-width: 767px) {
    .nav-bar {
        padding: 0.5rem 1rem;
    }

    .topnavbar {
        max-width: 120px;
    }
}

@media (min-width: 768px) and (max-width: 1023px) {
    .nav-bar {
        padding: 0.75rem 2rem;
    }
}

@media (min-width: 1024px) {
    .nav-bar {
        padding: 0.75rem 3rem;
    }
}
</style>